import * as React from "react";
import { Button, CloseButton } from "../Button";
import { expandBorderRadius, expandMargin } from "../utils/styles";
import { isFunction } from "../utils/primitives";
import { maybe } from "../utils/functional";
import { mergeOverrides } from "baseui";
import { stopPropagation } from "../utils/browser";
import { useStyletron } from "kanopi-components/theme/useStyletron";
import { useToggle } from "../hooks/toggle";
import { useUnderBreakpoint } from "../useUnderBreakpoint";
import {
  Modal as BaseModal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalButton,
  ModalProps as BaseModalProps,
} from "baseui/modal";

type ModalProps = BaseModalProps & {
  initialOpen?: boolean;
  open?: boolean;
  trigger?: React.ReactElement;
  triggerProp?: string;
  onCancel?: () => void;
  onProceed?: () => void;
};

export function Modal({
  initialOpen,
  open: forceOpen,
  trigger,
  triggerProp = "onClick",
  onCancel,
  onProceed,
  overrides = {},
  children,
  ...props
}: ModalProps) {
  const [open, toggleOpen] = useToggle(initialOpen);
  const isMobile = useUnderBreakpoint("medium");
  const [, theme] = useStyletron();
  const modalRef = React.createRef();
  const mergedOverrides = mergeOverrides(
    {
      Root: {
        style: {
          zIndex: theme.zIndex.modal,
        },
      },
      Dialog: {
        style: ({ $theme }) => ({
          ...expandBorderRadius($theme.borders.radius300),
          ...expandMargin("10px"),
          ...(isMobile ? {} : { width: "50vw" }),
        }),
        props: {
          ref: modalRef,
        },
      },
      Close: {
        component: CloseButton,
        props: {
          $size: Button.SIZE.mini,
          style: {
            position: "absolute",
            top: "10px",
            right: "10px",
          },
        },
      },
    },
    overrides
  );

  function handleClose(func) {
    return () => {
      toggleOpen();
      maybe(func)();
    };
  }

  const childProps = {
    onClose: handleClose(onCancel),
    onCancel: handleClose(onCancel),
    onProceed: handleClose(onProceed),
  };

  return (
    <ModalContext.Provider value={modalRef}>
      {trigger &&
        React.cloneElement(trigger, { [triggerProp]: stopPropagation(toggleOpen) })}
      <BaseModal
        onClose={childProps.onClose}
        isOpen={getOpen(open, forceOpen)}
        size={isMobile ? "default" : "auto"}
        overrides={mergedOverrides}
        {...props}
      >
        {isFunction(children) ? children(childProps) : children}
      </BaseModal>
    </ModalContext.Provider>
  );
}

function getOpen(open, forceOpen) {
  if (forceOpen !== undefined) {
    return forceOpen;
  }
  return open;
}

const ModalContext = React.createContext(null);

const useModalElem = () => {
  const modalRef = React.useContext(Modal.Context);
  const [current, setCurrent] = React.useState(modalRef?.current);
  React.useEffect(() => {
    setCurrent(modalRef?.current);
  }, [modalRef?.current]);
  return current;
};

Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
Modal.Button = ModalButton;
Modal.Context = ModalContext;
Modal.useElem = useModalElem;
